import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ButtonProps } from './index';

const useStyles = makeStyles<Theme, ButtonProps>(theme => ({
  autoWidth: {
    width: 'auto',
  },
  disableUnderline: {
    textDecoration: 'none',
  },
  error: {
    // `negative` layers a destructive skin over whichever `type` skin is already
    // on the button, so it has to outrank that skin — hence the doubled
    // selector. `&&` is (0,2,0) and beats `.secondary`; the `:hover` arm is
    // needed too, or `.secondary:hover` repaints the border gray400 the moment
    // you point at it.
    //
    // Plain `border` here is not enough: JSS emits these rules in key order, so
    // `.error` lands *before* `.secondary` in the same sheet and loses the tie
    // at equal specificity. That is how the outlined-red destructive button
    // shipped with a grey outline — only the border was wrong, because
    // `backgroundColor` and `color` below are props functions and JSS puts
    // those in the later dynamic sheet, where they already win.
    //
    // Disabled is excluded deliberately. MUI's `.MuiButton-outlined.Mui-disabled`
    // is *also* (0,2,0), and user `makeStyles` sheets are inserted after MUI
    // core's, so a bare `&&` would win there too and put a red outline on a
    // washed-out gray button — nothing else about which still reads as
    // destructive. Excluding it costs no specificity (`:not()` counts its
    // argument) and hands the disabled state back to the `type` skin, so it
    // matches every other disabled secondary button.
    '&&:not(.Mui-disabled), &&:not(.Mui-disabled):hover': {
      border: `1px solid ${theme.palette.common.error}`,
    },
    '&:hover': {
      backgroundColor: props => (props.type === 'primary' ? theme.palette.common.error : theme.palette.common.white),
    },
    backgroundColor: props => (props.type === 'primary' ? theme.palette.common.error : theme.palette.common.white),
    color: props => (props.type === 'primary' ? theme.palette.common.white : theme.palette.common.error),
  },
  fullWidth: {
    width: '100%',
  },
  large: {
    fontSize: theme.typography.button.fontSize,
    height: 48,
    width: props => (props.autoWidth || props.fullWidth ? undefined : 184),
  },
  maxWidth: {
    maxWidth: props => props.maxWidth,
  },
  medium: {
    fontSize: theme.typography.caption.fontSize,
    height: 40,
    width: props => (props.autoWidth || props.fullWidth ? undefined : 148),
  },
  primary: {
    '&:disabled': {
      backgroundColor: theme.palette.common.gray300,
      color: theme.palette.common.white,
    },
    '&:hover': {
      '@media (hover: none)': {
        background: theme.palette.primary.main,
      },
      background: theme.palette.common.blue600,
      boxShadow: '0px 1px 2px rgba(54, 56, 64, 0.06)',
    },
    background: theme.palette.primary.main,
    boxShadow: '0px 1px 2px rgba(54, 56, 64, 0.06)',
    color: theme.palette.primary.contrastText,
  },
  root: {
    '&:active': {
      boxShadow:
        '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    },

    borderRadius: theme.spacing(0.375),
    // TODO: define custom shadow in global styles
    boxShadow: '0 1px 3px 0 rgba(54, 56, 64, 0.12);',
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: theme.typography.h4.fontWeight,
  },
  secondary: {
    '&:disabled': {
      backgroundColor: theme.palette.common.gray300,
      color: theme.palette.common.gray800,
      opacity: 0.4,
    },
    '&:hover': {
      background: theme.palette.common.gray100,
      border: theme.customBorders.gray400,
      boxShadow: '0px 1px 0px rgba(19, 30, 64, 0.06)',
    },
    background: theme.palette.common.white,
    border: theme.customBorders.gray300,
    boxShadow: '0px 1px 0px rgba(19, 30, 64, 0.06)',
    color: theme.palette.common.gray800,
  },
  small: {
    fontSize: theme.typography.caption.fontSize,
    height: 32,
    lineHeight: theme.typography.h6.lineHeight,
    width: props => (props.autoWidth || props.fullWidth ? undefined : 124),
  },
  text: {
    '&$disableUnderline': {
      textDecoration: 'none',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: theme.palette.common.blue400,
    },
    boxShadow: 'none',
    color: theme.palette.primary.main,
    padding: 0,
    textDecoration: 'underline',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));

export default useStyles;
